import Modal from '~/components/Modal';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import DateTime from '~/lib/NewDateTime';
import { ordinal_suffix_of } from '~/lib/Utils';
import Select from '~/components/Select';
import FormContextProvider, {
    FormContext,
} from '~/components/forms/FormContext';
import ThemedForm from '~/components/forms/ThemedForm';
import useSharedPostApi from '~/lib/useSharedPostApi';
import Loading from '~/components/Loading';
import { useDebounce } from '@uidotdev/usehooks';
import { isAdminApp, isEmbedded } from '~/public/lib/PageHelpers.js';
import { XMarkIcon } from '@heroicons/react/24/outline';
import InfoMessageBlock from '~/components/InfoMessageBlock';
import {
    clientFormatDateLong,
    clientFormatDateTimeLong,
} from '~/public/lib/ClientDateTime.js';
import useClientTimezone from '~/public/lib/useClientTimezone.js';

const numTimeOpts = {};
for (let i = 2; i <= 52; i++) {
    numTimeOpts[i] = i;
}

function calculateRecurring(dateObj, repeat, numTimes) {
    let results = [dateObj];
    if (!repeat) {
        return results;
    }

    const parts = repeat.split('-');
    const repeatType = parts[0];
    const repeatSkip = parts[1];

    let newTime = dateObj;
    for (let i = 1 /*First time counts*/; i < numTimes; i++) {
        newTime = newTime.clone();
        if (repeatType === 'weekly') {
            newTime = newTime.add(repeatSkip, 'week');
        } else if (repeatType === 'monthly') {
            newTime = newTime.add(repeatSkip, 'month');
        } else if (repeatType === 'monthweek') {
            const hours = newTime.get('hour');
            const minutes = newTime.get('minute');

            const targetDow = dateObj.format('c');
            const monthStart = newTime.add(1, 'month').startOf('month');
            const monthStartDow = monthStart.format('c');

            let numWeeksToAdd = dateObj.weekdayWeekOfMonth() - 1;
            // If the starting day of the month is after the target day of the month
            //  then we need to add an extra week
            if (monthStartDow > targetDow) {
                numWeeksToAdd += 1;
            }

            newTime = monthStart.add(numWeeksToAdd, 'week');
            newTime = newTime.set({ hours, minutes });

            const startDow = newTime.format('c');

            newTime = newTime.add(targetDow - startDow, 'day');
        }

        results.push(newTime);
    }

    return results;
}

const TimeRow = ({
    time,
    availableResult,
    isChecking,
    hideTimes,
    isRemoved,
    toggleRemove,
    allowCustomTime,
    clientTz,
}) => {
    const result = availableResult[time.updateTimezone('UTC').toISOString()];
    time = time.updateTimezone(clientTz);

    return (
        <div className="mb-3 flex items-center justify-between">
            <div className={isRemoved ? 'line-through text-gray-500' : ''}>
                {time.formatDate()} {hideTimes ? '' : time.formatTime()}
            </div>
            <div className="flex items-center">
                {result === 'available' && (
                    <div
                        className={`tag bg-green-100 !text-green-600 ${isRemoved ? 'opacity-30' : ''}`}
                    >
                        Available
                    </div>
                )}
                {result === 'unavailable' && (
                    <div
                        className={`tag bg-red-100 !text-red-600 ${isRemoved ? 'opacity-30' : ''}`}
                    >
                        Unavailable
                    </div>
                )}
                {!result && !isChecking && (
                    <div className="tag bg-gray-300 text-black">Unknown</div>
                )}

                {isChecking && !result && <Loading />}
                {!isChecking && (result || allowCustomTime) && (
                    <button
                        onClick={() => toggleRemove(!isRemoved)}
                        type="button"
                        className="ml-2"
                    >
                        <XMarkIcon
                            className={`w-4 h-4 ${isRemoved ? 'text-gray-500' : 'text-red-600'}`}
                        />
                    </button>
                )}
            </div>
        </div>
    );
};

const ModalContainer = ({ children, isOpen, onClose }) => {
    if (isAdminApp()) {
        // Use a real modal
        return (
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                widthClass="sm:max-w-[700px]"
            >
                {children}
            </Modal>
        );
    } else {
        return (
            <div>
                <div className="fixed inset-0 z-40 w-screen overflow-y-auto">
                    <div
                        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                        aria-hidden="true"
                        onClick={onClose}
                    />
                </div>
                <div
                    className="absolute z-50 left-0 bg-white shadow-xl rounded-[23px] p-6 text-left transition-all sm:my-8 w-full sm:max-w-[700px] sm:mx-16 sm:p-6"
                    // isOpen={isOpen}
                    // onClose={doClose}
                    // widthClass="sm:max-w-[700px]"
                    // panelContainerClassName={isEmbedded() ? '!items-center' : ''}
                >
                    {children}
                </div>
            </div>
        );
    }
};

const RecurringModal = ({
    isOpen,
    onClose,
    time,
    value,
    onChange,
    eventType,
    bookableResourceId,
    hideTimes,
    allowCustomTime,
}) => {
    const { form } = useContext(FormContext);
    const clientTz = useClientTimezone();

    const [numTimes, setNumTimes] = useState('8');
    const [repeat, setRepeat] = useState();
    const selectRef = useRef();

    const [availableResult, setAvailableResult] = useState({});
    const [removedTimes, setRemovedTimes] = useState({});

    const dateObj = DateTime.fromString(time);
    const monthWeek = dateObj.weekdayWeekOfMonth();

    const { save: checkAvailable, isSaving: isChecking } = useSharedPostApi(
        `/scheduling/is-available`,
    );

    const repeatOpts = {
        'weekly-1': 'Every week',
        'weekly-2': 'Every other week',
        'weekly-3': 'Every 3 weeks',
        'weekly-4': 'Every 4 weeks',
        'weekly-6': 'Every 6 weeks',
        'weekly-8': 'Every 8 weeks',
        'monthly-1': `The ${ordinal_suffix_of(dateObj.format('d'))} of every month`,
        'monthweek-1': `The ${ordinal_suffix_of(monthWeek)} ${dateObj.format('cccc')} of every month`,
    };

    const timesNames = {
        'weekly-1': 'weeks',
        'monthly-1': 'months',
        'monthweek-1': 'months',
    };

    const allTimes = useMemo(() => {
        return calculateRecurring(dateObj, repeat, numTimes);
    }, [repeat, numTimes, time]);

    const hasUnavailableTimes =
        Object.values(availableResult).filter(
            result => result === 'unavailable',
        ).length > 0;

    const debouncedAllTimes = useDebounce(allTimes, 100);

    useEffect(() => {
        if (!isOpen || !selectRef.current) {
            return;
        }

        selectRef.current.focus();
    }, [isOpen, !!selectRef.current]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        checkAvailable({
            eventType: eventType.id,
            bookableResource: bookableResourceId,
            startTime: allTimes.map(t => t.updateTimezone('UTC').toISOString()),
        }).then(result => {
            setAvailableResult(result);
        });
    }, [debouncedAllTimes]);

    function saveRecurring() {
        // Add all the times that are available and user selected
        onChange(
            allTimes
                .map(t => t.updateTimezone('UTC').toISOString())
                .filter(t => {
                    const isAvailable = availableResult[t] !== 'unavailable';
                    // If it's not available, at least make sure we're booking as an admin
                    return (isAvailable || allowCustomTime) && !removedTimes[t];
                }),
        );
        onClose();
    }

    function doClose() {
        if (
            confirm(
                'You have not added any recurring times, are you sure you want to cancel adding more times?\n\nSelect "Continue" to save these times.',
            )
        ) {
            onClose();
        }
    }

    if (!isOpen) {
        return null;
    }

    /**
     * We aren't using a modal here because of issues when that's in an iframe
     * the modal takes over scrolling and also it's difficult to scroll into view
     * especially if we're double-iframed
     * instead we mimic a modal and absolute position right above the element where it's added
     * this also keeps the user with the ability to scroll
     */
    return (
        <ModalContainer isOpen={isOpen} onClose={doClose}>
            <ThemedForm styles={form?.settings?.styles}>
                <h2 className="text-center">Recurring</h2>
                <div className="text-center text-gray-500 text-lg">
                    {hideTimes
                        ? clientFormatDateLong(time, clientTz)
                        : clientFormatDateTimeLong(time, clientTz)}
                </div>
                <div className="sm:flex sm:space-x-6 mt-6">
                    <div className="flex-1">
                        <Select
                            ref={selectRef}
                            options={repeatOpts}
                            onChange={setRepeat}
                            value={repeat}
                            label="Repeat"
                            className="mb-4"
                            inputClassName="max-w-full"
                        />
                        <Select
                            options={numTimeOpts}
                            onChange={setNumTimes}
                            value={numTimes}
                            label="Times to Repeat"
                            className="mb-4"
                        />
                    </div>
                    <div className="flex-1 text-base">
                        {allTimes.map((t, idx) => (
                            <TimeRow
                                key={idx}
                                time={t}
                                clientTz={clientTz}
                                hideTimes={hideTimes}
                                availableResult={availableResult}
                                isChecking={isChecking}
                                isRemoved={
                                    !!removedTimes[
                                        t.updateTimezone('UTC').toISOString()
                                    ]
                                }
                                allowCustomTime={allowCustomTime}
                                toggleRemove={newIsRemoved => {
                                    setRemovedTimes(old => {
                                        const tIdx = t
                                            .updateTimezone('UTC')
                                            .toISOString();
                                        let res = { ...old };
                                        if (newIsRemoved) {
                                            res[tIdx] = true;
                                        } else {
                                            delete res[tIdx];
                                        }

                                        return res;
                                    });
                                }}
                            />
                        ))}
                    </div>
                </div>
                <div className={'mt-3 leading-tight'}>
                    This will repeat{' '}
                    <span className="font-semibold">{repeatOpts[repeat]}</span>{' '}
                    starting{' '}
                    {hideTimes
                        ? clientFormatDateLong(time, clientTz)
                        : clientFormatDateTimeLong(time, clientTz)}{' '}
                    for{' '}
                    <span className="font-semibold">
                        {numTimes} {timesNames[repeat] || 'times'}
                    </span>
                    .
                </div>
                {hasUnavailableTimes && allowCustomTime && (
                    <InfoMessageBlock showIcon={true} className="mt-4">
                        Some times above are outside your availability. You are
                        allowed to schedule unavailable times because you are an
                        admin.
                    </InfoMessageBlock>
                )}
                <div className="border-t space-x-4 mt-6 pt-6 flex items-center justify-center">
                    <button
                        type="button"
                        className={`${isAdminApp() ? 'btn-save' : 'form-button'} ${isChecking ? 'opacity-50' : ''}`}
                        onClick={saveRecurring}
                        disabled={isChecking}
                    >
                        {isChecking ? 'Checking availability...' : 'Continue'}
                    </button>
                    <button
                        type="button"
                        className={`${isAdminApp() ? 'btn-gray-pill-lg' : 'form-button-inverse'}`}
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                </div>
            </ThemedForm>
        </ModalContainer>
    );
};

export default RecurringModal;
